import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useUpdateLoadInfo } from '@editorHooks'
import { getSlabStiffening } from '@queries'
import { saveWindLoadResults } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { useQueriesLoading } from 'src/state/hooks'
import useInvalidateWindLoadResults from '../../hooks/useWindLoadsInvalidation'
import useWindloadsMutations from '../../hooks/useWindLoadsMutations'
import useWindLoadsQueries from '../../hooks/useWindLoadsQueries'
import { windLoadSettingsToFormValues } from '../../utils/windLoadFormUtils'
import WindLoads from './components/WindLoads'

const WindloadsTab = (): ReactElement => {
  const { projectId }: { projectId?: string } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const updateLoadInfo = useUpdateLoadInfo()
  const invalidateWindLoadResults = useInvalidateWindLoadResults()

  const { mutate, isSaving: isSavingWindLoads, isComputingWindLoads } = useWindloadsMutations()

  const { mutate: mutateManualWindloads, isLoading: isSavingManualWindLoads } = useMutation(
    (data: WindloadResults) => saveWindLoadResults.request(projectId, data),
    {
      onSuccess: () => {
        invalidateWindLoadResults(projectId as string)
        updateLoadInfo()
        enqueueSnackbar('Windlastkonfiguration erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Windlastkonfiguration'),
          { variant: 'error' },
        )
      },
    },
  )

  const slabStiffeningQuery = useQuery({
    queryKey: getSlabStiffening.getKey(projectId),
    queryFn: () => getSlabStiffening.request(projectId),
  })

  const queryResults = useWindLoadsQueries()

  const isLoading = useQueriesLoading(queryResults) || slabStiffeningQuery.isLoading
  const isFetching = useQueriesLoading(queryResults, 'isFetching') || slabStiffeningQuery.isFetching

  const isLoadingWindLoads = isLoading || isFetching

  if (!isLoading) {
    const [{ data: windLoadSettings }, { data: results }] = queryResults
    return (
      <WindLoads
        isLoading={isLoadingWindLoads}
        isSaving={isSavingWindLoads || isComputingWindLoads}
        data={windLoadSettingsToFormValues(windLoadSettings, results)}
        onSave={mutate}
        onSaveManualWindLoads={mutateManualWindloads}
        isLoadingManualWindLoads={isLoadingWindLoads}
        isSavingManualWindLoads={isSavingManualWindLoads}
        ignoredSlabs={
          slabStiffeningQuery.data && slabStiffeningQuery.data[0]?.stiffening === 'NotStiffening'
            ? [slabStiffeningQuery.data[0].element_guid]
            : []
        }
      />
    )
  } else {
    return <></>
  }
}

export default WindloadsTab
